import { useState } from "react";
import type { Answer, Attempt, ReviewQuestion } from "../lib/review";

type Props = {
	current: ReviewQuestion;
	attempt?: Attempt;
	selectedAnswer: Answer | undefined;
	shortAnswer: string;
	isGrading: boolean;
	gradingError: string;
	canSubmit: boolean;
	currentIndex: number;
	questionCount: number;
	onSelectAnswer: (answer: Answer) => void;
	onShortAnswerChange: (answer: string) => void;
	onSubmit: () => void;
};

export function QuestionCard({
	current,
	attempt,
	selectedAnswer,
	shortAnswer,
	isGrading,
	gradingError,
	canSubmit,
	currentIndex,
	questionCount,
	onSelectAnswer,
	onShortAnswerChange,
	onSubmit,
}: Props) {
	const [isHintVisible, setIsHintVisible] = useState(false);
	const question = current.question;
	const hintId = `${current.id}-hint`;
	return (
		<>
			<div className="question-meta">
				<span>{current.chapter.chapterTitle}</span>
				<span>
					Question {currentIndex + 1} of {questionCount}
				</span>
			</div>
			<article className="question-card">
				<div className="question-heading">
					<span className="question-number">{ordinal(currentIndex + 1)}</span>
					<div>
						<p className="question-type">{question.type.replace("-", " ")}</p>
						<h1>{question.question}</h1>
					</div>
				</div>
				{isHintVisible ? (
					<div className="question-hint" id={hintId} role="status">
						<strong>Hint</strong>
						<p>{question.hint}</p>
					</div>
				) : (
					<button
						className="hint-button"
						type="button"
						aria-controls={hintId}
						aria-expanded="false"
						onClick={() => setIsHintVisible(true)}
					>
						Show hint
					</button>
				)}
				{question.type === "multiple-choice" && (
					<fieldset className="answer-options" disabled={Boolean(attempt)}>
						<legend className="sr-only">Select one answer</legend>
						{question.options.map((option, index) => (
							<label
								className={`answer-option ${selectedAnswer === index ? "selected" : ""}`}
								key={option}
							>
								<input
									type="radio"
									name={current.id}
									checked={selectedAnswer === index}
									onChange={() => onSelectAnswer(index)}
								/>
								<span className="option-letter">
									{String.fromCharCode(65 + index)}
								</span>
								<span>{option}</span>
							</label>
						))}
					</fieldset>
				)}
				{question.type === "true-false" && (
					<fieldset className="true-false-options" disabled={Boolean(attempt)}>
						<legend className="sr-only">Select true or false</legend>
						{[true, false].map((value) => (
							<label
								className={`truth-option ${selectedAnswer === value ? "selected" : ""}`}
								key={String(value)}
							>
								<input
									type="radio"
									name={current.id}
									checked={selectedAnswer === value}
									onChange={() => onSelectAnswer(value)}
								/>
								<span>{value ? "True" : "False"}</span>
							</label>
						))}
					</fieldset>
				)}
				{question.type === "short-answer" && (
					<label className="short-answer">
						<span>Answer in your own words</span>
						<textarea
							value={shortAnswer}
							disabled={Boolean(attempt) || isGrading}
							onChange={(event) => onShortAnswerChange(event.target.value)}
							placeholder="Write what you remember from the lesson…"
							maxLength={4000}
							rows={5}
						/>
					</label>
				)}
				{!attempt && (
					<>
						{gradingError && (
							<p className="error-message" role="alert">
								{gradingError}
							</p>
						)}
						<button
							className="submit-button"
							type="button"
							disabled={!canSubmit || isGrading}
							onClick={onSubmit}
						>
							{isGrading ? "Checking your answer…" : "Submit answer"}
							{!isGrading && <span aria-hidden="true">→</span>}
						</button>
					</>
				)}
			</article>
		</>
	);
}

function ordinal(value: number) {
	return `${value}${value === 1 ? "st" : value === 2 ? "nd" : value === 3 ? "rd" : "th"}`;
}
